import * as React from "react";
import {clsx} from "clsx";
import {formatTime, getISOTime, parseTimeInput} from "@/shared/utils/datefunctions";
import {useDraftInput} from "@/shared/ui/primitives/useDraftInput";

interface TimeInputProps {
	id?: string;
	name?: string;
	/** Canonical backend time value (`HH:mm:ss`), or null when the field is empty. */
	value: string | null;
	/** Commits a normalized `HH:mm:ss` value, or null when the draft is empty or invalid. Only fired when the displayed time changed. */
	onValueChange: (value: string | null) => void;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	ariaLabel?: string;
	className?: string;
	disabled?: boolean;
}

export function isValidTimeInputValue(value: string | null): boolean {
	return Boolean(parseTimeInput(value));
}

/**
 * Text-based time input with local draft state and blur-time normalization.
 *
 * Consumers pass and receive backend time strings (`HH:mm:ss`) while users can type partial
 * display values without having the parent state reformatted on every keystroke.
 */
export function TimeInput({
	id,
	name,
	value,
	onValueChange,
	onFocus,
	ariaLabel,
	className,
	disabled = false
}: Readonly<TimeInputProps>) {
	const draftInputProps = useDraftInput<string | null>({
		value,
		format: v => formatTime(parseTimeInput(v)),
		parse: draft => {
			const parsed = parseTimeInput(draft);
			return parsed ? getISOTime(parsed) : null;
		},
		onValueChange,
		onFocus
	});

	return (
		<input
			id={id}
			type="text"
			name={name}
			className={clsx("form-control", className)}
			aria-label={ariaLabel}
			disabled={disabled}
			{...draftInputProps}
		/>
	);
}

export type {TimeInputProps};
